import clsx from "clsx";
import { AgeRequirementNotice } from "@/components/AgeRequirementNotice";
import { EmailInput } from "@/components/EmailInput";
import { FieldError } from "@/components/FieldError";
import { LicensePlateInput } from "@/components/LicensePlateInput";
import { PhoneInput } from "@/components/PhoneInput";
import { CountrySelect } from "@/components/CountrySelect";
import { BirthDateInput } from "@/components/BirthDateInput";
import { SalutationSelect } from "@/components/SalutationSelect";
import { StreetHouseNumberFields } from "@/components/StreetHouseNumberFields";
import type { CompanionFieldKey } from "@/lib/companionFields";
import { getCompanionFieldLabels } from "@/lib/companionFields";
import { useLocale } from "@/i18n";
import type { GooglePlacesAddressPatch } from "@/lib/googlePlaces";
import {
  activePrimaryGuestFields,
  type PrimaryGuestFieldsConfig,
  type PrimaryGuestFormData,
} from "@/lib/primaryGuestFields";
import { useId } from "react";

type Props = {
  form: PrimaryGuestFormData;
  config: PrimaryGuestFieldsConfig;
  onChange: (form: PrimaryGuestFormData) => void;
  heading?: string;
  description?: string;
  googlePlacesApiKey?: string | null;
  birthDateError?: string | null;
  showBirthDateError?: boolean;
  onBirthDateBlur?: () => void;
  onBirthDateEntryError?: (message: string | null) => void;
  /** Leere Pflichtfelder nach einem Weiter-Klick markieren. */
  highlightMissing?: boolean;
};

function isMissingValue(highlight: boolean, required: boolean, value: string): boolean {
  return highlight && required && value.trim().length === 0;
}

function fieldInputType(key: CompanionFieldKey): string {
  if (key === "email") return "email";
  if (key === "phone") return "tel";
  if (key === "date_of_birth") return "date";
  return "text";
}

function fieldAutoComplete(key: CompanionFieldKey): string | undefined {
  switch (key) {
    case "first_name":
      return "given-name";
    case "last_name":
      return "family-name";
    case "email":
      return "email";
    case "phone":
      return "tel";
    case "postal_code":
      return "postal-code";
    case "city":
      return "address-level2";
    case "country_code":
      return "country";
    default:
      return undefined;
  }
}

function fieldColSpan(key: CompanionFieldKey): string {
  if (
    key === "email" ||
    key === "company_name" ||
    key === "address_line_2"
  ) {
    return "block text-sm font-medium sm:col-span-2";
  }
  return "text-sm font-medium";
}

function usesStreetHouseRow(fields: CompanionFieldKey[], index: number): boolean {
  return fields[index] === "street" && fields[index + 1] === "house_number";
}

export function PrimaryGuestFieldsForm({
  form,
  config,
  onChange,
  heading,
  description,
  googlePlacesApiKey,
  birthDateError = null,
  showBirthDateError = false,
  onBirthDateBlur,
  onBirthDateEntryError,
  highlightMissing = false,
}: Props) {
  const { messages } = useLocale();
  const fieldLabels = getCompanionFieldLabels(messages);
  const resolvedHeading = heading ?? messages.fields.main_guest;
  const fields = activePrimaryGuestFields(config);
  const birthDateErrorId = useId();
  if (fields.length === 0) return null;

  const applyAddressAutocomplete = (patch: GooglePlacesAddressPatch) => {
    onChange({ ...form, ...patch });
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="bk-section-title">{resolvedHeading}</h3>
        {description ? <p className="bk-section-hint">{description}</p> : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((key, index) => {
          if (usesStreetHouseRow(fields, index)) {
            return (
              <StreetHouseNumberFields
                key="street-house_number"
                values={{ street: form.street, house_number: form.house_number }}
                streetRequired={config.street === "required"}
                houseNumberRequired={config.house_number === "required"}
                streetMissing={isMissingValue(highlightMissing, config.street === "required", form.street)}
                houseNumberMissing={isMissingValue(
                  highlightMissing,
                  config.house_number === "required",
                  form.house_number,
                )}
                onChange={(patch) => onChange({ ...form, ...patch })}
                googlePlacesApiKey={googlePlacesApiKey}
                onAddressAutocomplete={applyAddressAutocomplete}
              />
            );
          }
          if (key === "house_number" && index > 0 && fields[index - 1] === "street") {
            return null;
          }

          if (key === "street" && !usesStreetHouseRow(fields, index)) {
            return (
              <StreetHouseNumberFields
                key="street-only"
                values={{ street: form.street, house_number: form.house_number }}
                streetRequired={config.street === "required"}
                houseNumberRequired={false}
                streetMissing={isMissingValue(highlightMissing, config.street === "required", form.street)}
                showHouseNumber={false}
                onChange={(patch) => onChange({ ...form, ...patch })}
                googlePlacesApiKey={googlePlacesApiKey}
                onAddressAutocomplete={applyAddressAutocomplete}
              />
            );
          }

          const required = config[key] === "required";
          const label = `${fieldLabels[key]}${required ? " *" : ""}`;
          const missing = isMissingValue(highlightMissing, required, form[key]);
          const birthDateHasError = key === "date_of_birth" && showBirthDateError && birthDateError;
          const birthDateTooYoung =
            birthDateHasError && birthDateError === messages.validation.primaryAgeMin;
          if (key === "date_of_birth") {
            return (
              <div
                key={key}
                className="sm:col-span-2"
                data-missing-field={missing || (highlightMissing && birthDateHasError) ? "true" : undefined}
              >
                <BirthDateInput
                  variant="booking"
                  legend={label}
                  value={form.date_of_birth}
                  copy={messages.birthDate}
                  required={required}
                  missing={missing}
                  invalid={Boolean(birthDateHasError)}
                  invalidTone={birthDateTooYoung ? "requirement" : "error"}
                  describedBy={birthDateHasError ? birthDateErrorId : undefined}
                  onChange={(date_of_birth) => onChange({ ...form, date_of_birth })}
                  onBlur={() => onBirthDateBlur?.()}
                  onErrorChange={onBirthDateEntryError}
                />
                {birthDateTooYoung ? (
                  <AgeRequirementNotice id={birthDateErrorId}>{birthDateError}</AgeRequirementNotice>
                ) : birthDateHasError ? (
                  <FieldError id={birthDateErrorId}>{birthDateError}</FieldError>
                ) : null}
              </div>
            );
          }
          return (
            <label
              key={key}
              className={fieldColSpan(key)}
              data-missing-field={missing ? "true" : undefined}
            >
              {label}
              {key === "title" ? (
                <SalutationSelect
                  value={form.title}
                  onChange={(title) => onChange({ ...form, title })}
                  required={required}
                  missing={missing}
                />
              ) : key === "email" ? (
                <EmailInput
                  value={form.email}
                  onChange={(email) => onChange({ ...form, email })}
                  required={required}
                  missing={missing}
                  autoComplete={fieldAutoComplete(key)}
                />
              ) : key === "phone" ? (
                <PhoneInput
                  value={form.phone}
                  onChange={(phone) => onChange({ ...form, phone })}
                  required={required}
                  missing={missing}
                  autoComplete={fieldAutoComplete(key)}
                />
              ) : key === "license_plate" ? (
                <LicensePlateInput
                  value={form.license_plate}
                  onChange={(license_plate) => onChange({ ...form, license_plate })}
                  required={required}
                  missing={missing}
                />
              ) : key === "country_code" ? (
                <CountrySelect
                  value={form.country_code}
                  onChange={(country_code) => onChange({ ...form, country_code })}
                  required={required}
                  missing={missing}
                  autoComplete={fieldAutoComplete(key)}
                />
              ) : (
                <input
                  type={fieldInputType(key)}
                  className={clsx("bk-input mt-1", missing && "bk-input--missing")}
                  value={form[key]}
                  onChange={(e) => onChange({ ...form, [key]: e.target.value })}
                  autoComplete={fieldAutoComplete(key)}
                  data-address-field={key}
                  aria-invalid={missing || undefined}
                  required={required}
                />
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}
